<template>
  <div>
    <p class="cm-mg-p-wzd">1. {{ $t('common_words.general_information') }}</p>
    <hr />
    <div class="row">
      <div class="col-12 col-md-6">
        <div class="form-group has-label">
          <label>{{ $t('common_words.company') }}: *</label>
          <el-select
            v-model="model.business_id"
            clearable
            filterable
            class="select-primary custom-width-select required-business shadow-sm"
            size="large"
            :placeholder="`${$t('common_words.select')}...`"
            @change="onBusinessChange"
          >
            <el-option
              class="select-primary"
              v-for="business in businesses"
              :key="business.id"
              :value="business.id"
              :label="business.name"
            />
          </el-select>
        </div>
      </div>
      <div class="col-12 col-md-6">
        <div class="form-group has-label">
          <label>{{ $t('common_words.farm') }}: *</label>
          <el-select
            v-model="model.farm_id"
            clearable
            filterable
            class="select-primary custom-width-select required-farm shadow-sm"
            size="large"
            :placeholder="`${$t('common_words.select')}...`"
            @change="onFarmChange"
          >
            <el-option
              class="select-primary"
              v-for="farm in filteredFarms"
              :key="farm.id"
              :value="farm.id"
              :label="farm.name"
            />
          </el-select>
        </div>
      </div>
    </div>

    <p class="cm-mg-p-wzd text-center text-muted">
      {{ $t('production.crews_creation') }}
    </p>
    <hr />

    <div
      v-for="(_, index) in model.names"
      :key="`crew-row-${index}`"
      class="row align-items-start border rounded-sm shadow-sm py-3 px-2 mb-3 crew-row"
    >
      <div class="col-12 col-md-5">
        <fg-input
          v-model="model.names[index]"
          type="text"
          :label="`${$t('common_words.name')}: *`"
          :name="`common_words.name_${index}`"
          :error="getFieldErrorByIndex('common_words.name', true, index)"
          v-validate="modelValidations.names"
          :placeholder="`${$t('common_words.name')}...`"
          @blur="convertUppercase(index)"
        />
      </div>
      <div class="col-10 col-md-6">
        <div class="form-group has-label">
          <label>{{ $t('labor.workers') }}:</label>
          <el-select
            v-model="model.worker_ids[index]"
            @change="
              selectAllFromSelect(
                'model.worker_ids[' + index + ']',
                availableWorkers
              )
            "
            multiple
            filterable
            clearable
            collapse-tags
            class="select-primary custom-width-select shadow-sm"
            size="large"
            :placeholder="`${$t('common_words.select')}...`"
          >
            <el-option
              class="select-primary selected-all"
              v-for="worker in availableWorkers"
              :key="worker.id"
              :value="worker.id"
              :label="worker.name"
            />
          </el-select>
        </div>
      </div>
      <div class="col-2 col-md-1 pt-4 text-center">
        <a
          v-tooltip.top-center="$t('common_words.add')"
          class="btn-success btn-simple btn-link custom-margin-actions"
          @click="addCrew(index)"
        >
          <em class="fa fa-plus custom-size-fa-actions"></em>
        </a>
        <a
          v-if="model.names.length > 1"
          v-tooltip.top-center="$t('common_words.erase')"
          class="btn-danger btn-simple btn-link custom-margin-actions"
          @click="removeCrew(index)"
        >
          <em class="fa fa-times custom-size-fa-actions"></em>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { Axios } from 'src/class/Axios.js';
import { isset, convertFirstLetterUppercase } from 'src/util/Util';
import MixinAlert from 'src/mixins/mixin-alert';
import MixinForm from 'src/mixins/forms/mixin-form';
import MixinLoading from 'src/mixins/mixin-loading';
import MixinMasters from 'src/util/mixinMasters';
import MixinPermissions from 'src/util/mixinPermissions';
import UtilFactory from 'src/factories/UtilFactory';

export default {
  name: 'FirstStep',
  mixins: [MixinAlert, MixinForm, MixinLoading, MixinMasters, MixinPermissions],
  data() {
    return {
      businesses: [],
      farms: [],
      workers: [],
      model: {
        business_id: null,
        farm_id: null,
        user_id: null,
        ids: [null],
        names: [''],
        worker_ids: [[]],
      },
      modelValidations: {
        names: {
          required: true,
        },
      },
    };
  },
  computed: {
    filteredFarms() {
      if (!isset(this.model.business_id)) return this.farms;
      return this.farms.filter((farm) => {
        if (farm.business_id == this.model.business_id) return true;
        const relations = farm.businesses || farm.business || [];
        const businesses = Array.isArray(relations) ? relations : [relations];
        if (
          !isset(farm.business_id) &&
          businesses.filter(Boolean).length === 0
        ) {
          return true;
        }
        return businesses.some(
          (business) => business && business.id == this.model.business_id
        );
      });
    },
    availableWorkers() {
      if (!this.workers || !this.workers.length) return this.concatAll([]);

      let list = this.workers;
      if (isset(this.model.business_id)) {
        const filteredBiz = this.workers.filter(
          (w) => w.business_id == this.model.business_id
        );
        if (filteredBiz.length > 0) list = filteredBiz;
      }

      const formatted = list.map((w) => ({
        ...w,
        name: w.code ? `${w.code} - ${w.name}` : w.name,
      }));

      return this.concatAll(formatted);
    },
  },
  methods: {
    convertUppercase(index) {
      const name = convertFirstLetterUppercase(this.model.names[index]);

      this.$set(this.model.names, index, name);
    },

    selectAllFromSelect(model, array_data, scope = null) {
      let util_selects = new UtilFactory(this).getUtilObject('select');
      util_selects.selectAllFromSelect(model, array_data, scope);
      if (typeof model === 'string' && model.includes('[')) {
        const match = model.match(/model\.(\w+)\[(\d+)\]/);
        if (match) {
          const prop = match[1];
          const idx = parseInt(match[2], 10);
          if (this.model && Array.isArray(this.model[prop])) {
            this.$set(this.model[prop], idx, [...this.model[prop][idx]]);
          }
        }
      }
    },
    onBusinessChange() {
      this.model.farm_id = null;
      this.cleanInvalidWorkers();
    },
    onFarmChange() {
      this.cleanInvalidWorkers();
    },
    cleanInvalidWorkers() {
      const validWorkerIds = new Set(
        this.availableWorkers.filter((w) => w.id !== 0).map((w) => w.id)
      );
      this.model.worker_ids = this.model.worker_ids.map((ids) => {
        if (!Array.isArray(ids)) return [];
        return ids.filter((id) => id !== 0 && validWorkerIds.has(id));
      });
    },
    addCrew(index) {
      this.model.ids.splice(index + 1, 0, null);
      this.model.names.splice(index + 1, 0, '');
      this.model.worker_ids.splice(index + 1, 0, []);
    },
    removeCrew(index) {
      this.model.ids.splice(index, 1);
      this.model.names.splice(index, 1);
      this.model.worker_ids.splice(index, 1);
    },
    validate() {
      return this.$validator.validateAll().then((valid) => {
        if (!valid) return false;
        if (!isset(this.model.business_id)) {
          return this.validationInputsRequired('business');
        }
        if (!isset(this.model.farm_id)) {
          return this.validationInputsRequired('farm');
        }

        const business = this.businesses.find(
          (item) => item.id == this.model.business_id
        );
        const farm = this.farms.find((item) => item.id == this.model.farm_id);

        const workerNames = this.model.worker_ids.map((workerIdList) => {
          if (!Array.isArray(workerIdList)) return [];
          return workerIdList
            .filter((id) => id !== 0 && isset(id))
            .map((id) => {
              const worker = this.workers.find((w) => w.id == id);
              return worker
                ? worker.code
                  ? `${worker.code} - ${worker.name}`
                  : worker.name
                : id;
            });
        });

        const user =
          this.$store.state.auth.user ||
          JSON.parse(sessionStorage.getItem('user') || '{}');
        const userId = user && user.id ? user.id : null;

        const crews = this.model.names.map((name, index) => ({
          id: this.model.ids[index] || null,
          name: name,
          worker_ids: (this.model.worker_ids[index] || []).filter(
            (id) => id !== 0 && isset(id)
          ),
          worker_names: workerNames[index] || [],
          user_id: userId,
        }));

        this.$emit('on-validated', {
          ...this.model,
          user_id: userId,
          business_name: business ? business.name : null,
          farm_name: farm ? farm.name : null,
          worker_names: workerNames,
          crews: crews,
        });
        return true;
      });
    },
    setEditForm(editForm) {
      const business = editForm.business || (editForm.businesses || [])[0];
      const farm = editForm.farm || (editForm.farms || [])[0];
      const crews = editForm.crews || [editForm];
      const rows = Array.isArray(crews) && crews.length ? crews : [editForm];

      const businessId =
        editForm.business_id ||
        (business && business.id) ||
        (rows[0] && rows[0].business_id) ||
        null;
      const farmId =
        editForm.farm_id ||
        (farm && farm.id) ||
        (rows[0] && rows[0].farm_id) ||
        null;

      const user =
        this.$store.state.auth.user ||
        JSON.parse(sessionStorage.getItem('user') || '{}');
      const userId = editForm.user_id || (user && user.id ? user.id : null);

      this.model = {
        business_id: businessId,
        farm_id: farmId,
        user_id: userId,
        ids: rows.map((r) => r.id || null),
        names: rows.map((r) => r.name || ''),
        worker_ids: rows.map((r) => {
          if (Array.isArray(r.worker_ids))
            return r.worker_ids.filter((id) => id !== 0 && isset(id));
          if (Array.isArray(r.workers))
            return r.workers
              .map((w) => w.id || w)
              .filter((id) => id !== 0 && isset(id));
          return [];
        }),
      };
    },
  },
  mounted() {
    Promise.all([
      Axios.getMasters('businesses/only'),
      Axios.getMasters('farms/farms/only'),
      Axios.getMasters('works/workers/indexField'),
    ])
      .then(([businesses, farms, workers]) => {
        this.businesses = businesses.data.data;
        this.farms = farms.data.data;
        this.workers = workers.data.data || [];
      })
      .catch((error) => this.showMessageErrors(error));

    if (this.isUpdate() && sessionStorage.getItem('editForm')) {
      this.setEditForm(JSON.parse(sessionStorage.getItem('editForm')));
    }
  },
};
</script>

<style scoped>
.crew-row {
  background-color: #fcfffb !important;
}
</style>
